import { Link } from "react-router-dom";
import AdminLayout from "./components/AdminLayout";
import { useAdminData } from "../../store/adminDataStore";
import { useAdminT } from "../../hooks/useAdminT";
import { ApiModeBanner } from "./components/AdminUI";

export default function AdminDashboard() {
  const { state, resetToDefaults, isApiMode } = useAdminData();
  const t = useAdminT();

  const activeServices = state.services.filter(s => s.is_active).length;
  const publishedPortfolio = state.portfolioItems.filter(p => p.is_published).length;
  const showreelItems = state.portfolioItems.filter(p => p.is_showreel && p.is_published).length;
  const featuredItems = state.portfolioItems.filter(p => p.is_featured && p.is_published).length;
  const activeContent = state.homepageSections.filter(h => h.is_active).length;
  const serviceOptions = state.serviceOptions.filter(o => o.is_active).length;
  const draftPortfolio = state.portfolioItems.filter(p => !p.is_published).length;
  const inactiveServices = state.services.filter(s => !s.is_active).length;

  const STATS = [
    { label: t("tabifotoAdmin.dashboard.active_services"), value: activeServices, total: state.services.length, icon: "ri-camera-line", path: "/admin/services" },
    { label: t("tabifotoAdmin.dashboard.published_portfolio"), value: publishedPortfolio, total: state.portfolioItems.length, icon: "ri-gallery-line", path: "/admin/portfolio" },
    { label: t("tabifotoAdmin.dashboard.on_showreel"), value: showreelItems, total: publishedPortfolio, icon: "ri-film-line", path: "/admin/homepage" },
    { label: t("tabifotoAdmin.dashboard.featured_works"), value: featuredItems, total: publishedPortfolio, icon: "ri-star-line", path: "/admin/homepage" },
    { label: t("tabifotoAdmin.dashboard.active_content"), value: activeContent, total: state.homepageSections.length, icon: "ri-file-text-line", path: "/admin/content" },
    { label: t("tabifotoAdmin.dashboard.service_options"), value: serviceOptions, total: state.serviceOptions.length, icon: "ri-settings-3-line", path: "/admin/services" },
  ];

  const MODULES = [
    { label: t("tabifotoAdmin.nav.services"), icon: "ri-camera-line", path: "/admin/services", desc: t("tabifotoAdmin.services.subtitle") },
    { label: t("tabifotoAdmin.nav.portfolio"), icon: "ri-gallery-line", path: "/admin/portfolio", desc: t("tabifotoAdmin.portfolio.subtitle") },
    { label: t("tabifotoAdmin.nav.homepage_display"), icon: "ri-layout-line", path: "/admin/homepage", desc: t("tabifotoAdmin.homepage.subtitle") },
    { label: t("tabifotoAdmin.nav.content"), icon: "ri-file-text-line", path: "/admin/content", desc: t("tabifotoAdmin.content.subtitle") },
    { label: t("tabifotoAdmin.nav.orders"), icon: "ri-file-list-3-line", path: "/admin/orders", desc: t("tabifotoAdmin.orders.subtitle") },
    { label: t("tabifotoAdmin.nav.photographers"), icon: "ri-user-star-line", path: "/admin/photographers", desc: "" },
  ];

  return (
    <AdminLayout title={t("tabifotoAdmin.dashboard.title")} subtitle={t("tabifotoAdmin.dashboard.subtitle")}>
      <ApiModeBanner isApiMode={isApiMode} />

      {/* Stat grid */}
      <div className="grid grid-cols-6 gap-3 mb-6">
        {STATS.map(s => (
          <Link key={s.label} to={s.path}
            className="bg-white rounded-lg border border-[#D9D9D9]/60 p-4 hover:border-[#C8B8A6]/60 transition-colors cursor-pointer"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="w-7 h-7 flex items-center justify-center text-[#C8B8A6]">
                <i className={`${s.icon} text-base`} />
              </div>
              <span className="text-xs text-[#2C2C2C]/25 font-light">/ {s.total}</span>
            </div>
            <p className="text-2xl font-extralight text-[#2C2C2C]">{s.value}</p>
            <p className="text-xs text-[#2C2C2C]/40 font-light mt-0.5 leading-tight">{s.label}</p>
          </Link>
        ))}
      </div>

      {/* Warnings */}
      {(draftPortfolio > 0 || inactiveServices > 0) && (
        <div className="mb-5 bg-[#C8B8A6]/10 border border-[#C8B8A6]/30 rounded-lg px-5 py-3 flex items-center gap-3">
          <div className="w-4 h-4 flex items-center justify-center text-[#C8B8A6] shrink-0">
            <i className="ri-information-line" />
          </div>
          <p className="text-sm text-[#2C2C2C]/60 font-light">
            {[
              draftPortfolio > 0 && t("tabifotoAdmin.dashboard.drafts_warning", { count: draftPortfolio }),
              inactiveServices > 0 && t("tabifotoAdmin.dashboard.inactive_warning", { count: inactiveServices }),
            ].filter(Boolean).join(" · ")}
          </p>
        </div>
      )}

      {/* Module grid */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {MODULES.map(m => (
          <Link key={m.path} to={m.path}
            className="group bg-white rounded-lg border border-[#D9D9D9]/60 p-5 hover:border-[#C8B8A6]/60 transition-all cursor-pointer"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 flex items-center justify-center text-[#2C2C2C]/30 group-hover:text-[#C8B8A6] transition-colors">
                <i className={`${m.icon} text-lg`} />
              </div>
              <span className="text-sm font-light text-[#2C2C2C]">{m.label}</span>
            </div>
            <p className="text-xs text-[#2C2C2C]/40 font-light pl-11 line-clamp-2">{m.desc}</p>
          </Link>
        ))}
      </div>

      {/* Data model reference */}
      <div className="bg-white rounded-lg border border-[#D9D9D9]/60 p-5 mb-4">
        <p className="text-xs uppercase tracking-wider text-[#2C2C2C]/30 mb-3">{t("tabifotoAdmin.dashboard.data_tables")}</p>
        <div className="grid grid-cols-4 gap-3">
          {[
            { table: "services", count: state.services.length, icon: "ri-camera-line" },
            { table: "service_options", count: state.serviceOptions.length, icon: "ri-settings-3-line" },
            { table: "portfolio_items", count: state.portfolioItems.length, icon: "ri-gallery-line" },
            { table: "homepage_sections", count: state.homepageSections.length, icon: "ri-layout-line" },
          ].map(tb => (
            <div key={tb.table} className="flex items-center gap-2 p-3 bg-[#F7F6F3] rounded">
              <div className="w-5 h-5 flex items-center justify-center text-[#C8B8A6]">
                <i className={`${tb.icon} text-sm`} />
              </div>
              <div>
                <p className="text-xs font-mono text-[#2C2C2C]/50">{tb.table}</p>
                <p className="text-sm font-light text-[#2C2C2C]">{tb.count} rows</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Reset (only in mock mode) */}
      {!isApiMode && (
        <div className="bg-white rounded-lg border border-[#D9D9D9]/60 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-light text-[#2C2C2C]">{t("tabifotoAdmin.dashboard.reset_title")}</p>
              <p className="text-xs text-[#2C2C2C]/40 mt-0.5">{t("tabifotoAdmin.dashboard.reset_desc")}</p>
            </div>
            <button onClick={resetToDefaults}
              className="px-4 py-2 rounded-md text-xs border border-[#D9D9D9] text-[#2C2C2C]/50 hover:border-red-300 hover:text-red-400 transition-colors cursor-pointer whitespace-nowrap">
              {t("tabifotoAdmin.dashboard.reset_button")}
            </button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
